import { useState, useEffect } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { skillTemplateService, type SkillTemplate } from '@/services/skill-template-service';
import {
  Header,
  ContentContainer,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { Plus, Edit, Trash2, MoreHorizontal, RefreshCw, FileText } from '@/lib/icons';
import { format } from 'date-fns';

export function KaiyaSkillsPage() {
  const { setShowPageHeader } = usePageContext();
  const [templateToDelete, setTemplateToDelete] = useState<SkillTemplate | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data, isLoading } = useQuery({
    queryKey: ['skillTemplates'],
    queryFn: () => skillTemplateService.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => skillTemplateService.destroy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTemplates'] });
      toast.success('Skill template deleted');
      setTemplateToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete skill template');
    },
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => skillTemplateService.sync(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTemplates'] });
      toast.success('Skill template synced to Anthropic');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to sync skill template');
    },
  });

  const templates: SkillTemplate[] = extractAttributesList(data);

  const getSyncBadge = (template: SkillTemplate) => {
    if (!template.provider_sync_status?.length) {
      return <Badge variant="outline">Not synced</Badge>;
    }

    const anthropicSync = template.provider_sync_status.find(
      (s) => s.provider === 'anthropic'
    );
    if (!anthropicSync) return <Badge variant="outline">Not synced</Badge>;

    switch (anthropicSync.sync_status) {
      case 'synced':
        return <Badge variant="default">Synced</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  return (
    <>
      <Header
        title="Kaiya Skill Templates"
        description="Manage AI skill templates following the Agent Skills open standard"
      />

      <ContentContainer>
        <div className="flex justify-end mb-4">
          <Button onClick={() => navigate('/kaiya-skills/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Skill Template
          </Button>
        </div>

        {isLoading ? (
          <Empty>
            <Spinner />
          </Empty>
        ) : templates.length === 0 ? (
          <Empty>
            <EmptyIcon>
              <FileText className="h-12 w-12" />
            </EmptyIcon>
            <EmptyTitle>No skill templates</EmptyTitle>
            <EmptyDescription>
              Create your first skill template to get started with Kaiya's document generation capabilities.
            </EmptyDescription>
            <Button onClick={() => navigate('/kaiya-skills/new')} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              New Skill Template
            </Button>
          </Empty>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Provider Sync</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{template.display_title}</div>
                          <div className="text-sm text-muted-foreground">{template.name}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{template.skill_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          template.execution_mode === 'deliverable' ? 'default' :
                          template.execution_mode === 'guided' ? 'secondary' : 'outline'
                        }>
                          {template.execution_mode}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getSyncBadge(template)}</TableCell>
                      <TableCell>v{template.current_version_number ?? 0}</TableCell>
                      <TableCell>
                        {template.updated_at
                          ? format(new Date(template.updated_at), 'MMM d, yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/kaiya-skills/${template.id}/edit`)}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => syncMutation.mutate(template.id)}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Sync to Anthropic
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setTemplateToDelete(template)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </ContentContainer>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!templateToDelete}
        onOpenChange={() => setTemplateToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Skill Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{templateToDelete?.display_title}"? This
              action cannot be undone and will remove all versions and provider mappings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => templateToDelete && deleteMutation.mutate(templateToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
